// Gelieferte Unterlagen mit Rückmeldung: erkannter Typ, Jahr, Konzessionär, Entgelte – jeweils mit
// Fundstelle (Seite und wörtliches Zitat). Nicht wörtlich gefundene Angaben werden markiert.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DB_PORTAL_TEXTBAUSTEINE, type DbPortalDokument, type DbPortalFundstelle, type DbPortalLieferung, type DbPortalLieferungListe } from '@gql/shared-types';
import { api, API_BASIS } from '../api/client.js';
import {
  ART_TEXT,
  datumText,
  DOK_STATUS_TEXT,
  entgeltText,
  euro,
  groesse,
  JN_TEXT,
  KOMPONENTEN_TEXT,
  komponentenMieteText,
  komponentenSummeText,
  LIEFER_STATUS_TEXT,
  prozentText,
  zeitpunkt,
} from '../daten/lieferungen.js';
import { punktTitel } from '../daten/standorte.js';

function Fundstelle({ f }: { f: DbPortalFundstelle | null }) {
  if (!f) return null;
  return (
    <span className={`fundstelle${f.belegt ? '' : ' fundstelle-unbelegt'}`}>
      {f.seite ? `S. ${f.seite}: ` : ''}„{f.zitat}“{!f.belegt && <strong> – nicht wörtlich im Text gefunden, bitte prüfen</strong>}
    </span>
  );
}

function Angabe({ titel, wert, f }: { titel: string; wert: string | null | undefined; f?: DbPortalFundstelle | null }) {
  if (!wert) return null;
  return (
    <div className="angabe">
      <dt>{titel}</dt>
      <dd>
        {wert}
        {f && (
          <details className="fundstelle-details">
            <summary>Fundstelle</summary>
            <Fundstelle f={f} />
          </details>
        )}
      </dd>
    </div>
  );
}

function DokumentZeile({ d }: { d: DbPortalDokument }) {
  const ex = d.extraktion;
  const laeuft = d.status === 'wartet' || d.status === 'in_arbeit';
  return (
    <li className={`dok dok-${d.status}`}>
      <div className="dok-kopf">
        <a className="dok-name" href={`${API_BASIS}/dokumente/${d.id}/datei`} download>
          {d.zipPfad ?? d.dateiname}
        </a>
        <span className="zelle-hinweis">
          {groesse(d.groesse)}
          {d.herkunft === 'kamera' ? ' · Foto' : d.herkunft === 'zip' ? ' · aus ZIP' : ''}
          {d.ocr ? ' · Texterkennung (OCR)' : ''}
        </span>
        <span className={`chip chip-${d.status}`}>
          {laeuft && <span className="spinner spinner-klein" aria-hidden="true" />} {DOK_STATUS_TEXT[d.status]}
        </span>
      </div>
      {d.fehler && <p className="fehlertext">{d.fehler}</p>}
      {ex && (
        <>
          <dl className="angaben">
            <Angabe titel="Erkannt als" wert={`${ART_TEXT[ex.art]}${ex.jahr ? ` ${ex.jahr}` : ''}${ex.artSicherheit !== 'hoch' ? ` (Sicherheit ${ex.artSicherheit})` : ''}`} />
            {d.checkliste && <Angabe titel="Checkliste" wert={`abgehakt: ${punktTitel(d.checkliste)}`} />}
            <Angabe titel="Konzessionär" wert={ex.konzessionaer.wert} f={ex.konzessionaer.fundstelle} />
            <Angabe titel="Vertragsnummer" wert={ex.vertragsnummer.wert} f={ex.vertragsnummer.fundstelle} />
            <Angabe titel="Rechnungsnummer" wert={ex.rechnungsnummer.wert} f={ex.rechnungsnummer.fundstelle} />
            <Angabe titel="Datum" wert={ex.dokumentDatum.wert ? datumText(ex.dokumentDatum.wert) : null} f={ex.dokumentDatum.fundstelle} />
            <Angabe
              titel="Laufzeit/Zeitraum"
              wert={ex.laufzeit.beginn || ex.laufzeit.ende ? [ex.laufzeit.beginn, ex.laufzeit.ende].map((d) => (d ? datumText(d) : '…')).join(' – ') : ex.laufzeit.text}
              f={ex.laufzeit.fundstelle}
            />
            <Angabe titel="Serviceklasse" wert={ex.serviceklasse.wert} f={ex.serviceklasse.fundstelle} />
            <Angabe titel="Objekt" wert={ex.standortHinweis.wert} f={ex.standortHinweis.fundstelle} />
            {ex.entgeltaenderung && (
              <>
                <Angabe titel="Gültig ab" wert={ex.entgeltaenderung.gueltigAb.wert ? datumText(ex.entgeltaenderung.gueltigAb.wert) : null} f={ex.entgeltaenderung.gueltigAb.fundstelle} />
                <Angabe titel="Entgelt bisher" wert={ex.entgeltaenderung.bisher.cent !== null ? `${euro(ex.entgeltaenderung.bisher.cent)} monatlich netto` : null} f={ex.entgeltaenderung.bisher.fundstelle} />
                <Angabe titel="Entgelt neu" wert={ex.entgeltaenderung.neu.cent !== null ? `${euro(ex.entgeltaenderung.neu.cent)} monatlich netto` : null} f={ex.entgeltaenderung.neu.fundstelle} />
                <Angabe
                  titel="Erhöhung"
                  wert={
                    ex.entgeltaenderung.erhoehung.cent !== null
                      ? [
                          euro(ex.entgeltaenderung.erhoehung.cent),
                          ex.entgeltaenderung.prozent !== null ? `(${prozentText(ex.entgeltaenderung.prozent)})` : '',
                          ex.entgeltaenderung.stimmig ? '– rechnerisch stimmig' : ex.entgeltaenderung.erhoehung.text === null ? '– berechnet aus neu minus bisher' : '– bitte prüfen',
                        ]
                          .filter(Boolean)
                          .join(' ')
                      : null
                  }
                  f={ex.entgeltaenderung.erhoehung.fundstelle}
                />
                <Angabe titel="Erhöhungssätze laut Schreiben" wert={ex.entgeltaenderung.saetze.wert} f={ex.entgeltaenderung.saetze.fundstelle} />
                {ex.entgeltaenderung.anteil && (
                  <Angabe
                    titel="Anteil Sicherheits-/Zeitsysteme (ohne Leitungsgebühren)"
                    wert={
                      [
                        ex.entgeltaenderung.anteil.bisher.cent !== null ? `bisher ${euro(ex.entgeltaenderung.anteil.bisher.cent)}` : null,
                        ex.entgeltaenderung.anteil.neu.cent !== null ? `neu ${euro(ex.entgeltaenderung.anteil.neu.cent)}` : null,
                        ex.entgeltaenderung.anteil.prozent !== null ? `(${prozentText(ex.entgeltaenderung.anteil.prozent)})` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || null
                    }
                    f={ex.entgeltaenderung.anteil.neu.fundstelle ?? ex.entgeltaenderung.anteil.bisher.fundstelle}
                  />
                )}
              </>
            )}
          </dl>
          {ex.entgelte.length > 0 && (
            <div className="tabelle-rahmen">
              <table className="tabelle tabelle-klein tabelle-karten">
                <caption className="sr-only">Entgelte</caption>
                <thead>
                  <tr>
                    <th scope="col">Entgelt</th>
                    <th scope="col">Betrag</th>
                    <th scope="col">Fundstelle</th>
                  </tr>
                </thead>
                <tbody>
                  {ex.entgelte.map((e, i) => (
                    <tr key={i}>
                      <td data-label="Entgelt">{e.bezeichnung}</td>
                      <td data-label="Betrag" className="zahl">
                        {entgeltText(e)}
                      </td>
                      <td data-label="Fundstelle">
                        <Fundstelle f={e.fundstelle} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {ex.positionen.length > 0 && (
            <div className="tabelle-rahmen">
              <table className="tabelle tabelle-klein tabelle-karten">
                <caption className="sr-only">Rechnungspositionen</caption>
                <thead>
                  <tr>
                    <th scope="col">Position</th>
                    <th scope="col">Menge</th>
                    <th scope="col">Einzelpreis</th>
                    <th scope="col">Gesamt</th>
                  </tr>
                </thead>
                <tbody>
                  {ex.positionen.map((p, i) => (
                    <tr key={i}>
                      <td data-label="Position">
                        {p.bezeichnung}
                        {p.fundstelle && !p.fundstelle.belegt && <Fundstelle f={p.fundstelle} />}
                      </td>
                      <td data-label="Menge">{p.menge ?? '–'}</td>
                      <td data-label="Einzelpreis" className="zahl">
                        {p.einzelpreisText ?? '–'}
                      </td>
                      <td data-label="Gesamt" className="zahl">
                        {p.gesamtCent !== null ? euro(p.gesamtCent) : (p.gesamtText ?? '–')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(ex.komponenten ?? []).map((t, n) => (
            <div className="tabelle-rahmen" key={`komponenten-${n}`}>
              <p className="zelle-hinweis">
                <strong>Komponentenpreise{(ex.komponenten?.length ?? 0) > 1 ? ` (Tabelle ${n + 1})` : ''}</strong>
                {komponentenSummeText(t) ? ` – ${komponentenSummeText(t)}` : ''}
              </p>
              <table className="tabelle tabelle-klein tabelle-karten">
                <caption className="sr-only">Komponentenpreise{(ex.komponenten?.length ?? 0) > 1 ? ` (Tabelle ${n + 1})` : ''}</caption>
                <thead>
                  <tr>
                    <th scope="col">Komponente</th>
                    <th scope="col">Menge</th>
                    <th scope="col">Miete monatlich</th>
                    <th scope="col">Einrichtung</th>
                  </tr>
                </thead>
                <tbody>
                  {t.zeilen.map((z, i) => (
                    <tr key={i}>
                      <td data-label="Komponente">
                        {z.bezeichnung}
                        <span className="zelle-hinweis"> · {KOMPONENTEN_TEXT[z.art]}</span>
                        {z.fundstelle && !z.fundstelle.belegt && <Fundstelle f={z.fundstelle} />}
                      </td>
                      <td data-label="Menge">{z.mengeText ?? '–'}</td>
                      <td data-label="Miete monatlich" className="zahl">
                        {komponentenMieteText(z)}
                      </td>
                      <td data-label="Einrichtung" className="zahl">
                        {z.einrichtungCent !== null ? euro(z.einrichtungCent) : '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {(ex.hinweise.length > 0 || ex.textGekuerzt) && (
            <ul className="dok-hinweise">
              {ex.textGekuerzt && <li>Das Dokument ist sehr lang; ausgewertet wurde nur der Anfang.</li>}
              {ex.hinweise.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          )}
          <p className="zelle-hinweis">{ex.quelle === 'ki' ? 'Ausgewertet mit KI (Verarbeitung in der EU) – Angaben bitte prüfen.' : 'Automatische Regelauswertung – Angaben sind Vorschläge.'}</p>
        </>
      )}
    </li>
  );
}

export function LieferungKarte({ l, mitStandort, kinder }: { l: DbPortalLieferung; mitStandort?: boolean; kinder?: React.ReactNode }) {
  return (
    <article className={`lieferung lieferung-${l.status}`} aria-label={`Lieferung vom ${zeitpunkt(l.angelegtAm)}`}>
      <header className="lieferung-kopf">
        <h3>
          {mitStandort ? <Link to={`/standorte/${l.standortId}`}>{l.standortName}</Link> : 'Lieferung'} · {zeitpunkt(l.angelegtAm)}
        </h3>
        <span className={`chip chip-l-${l.status}`}>{LIEFER_STATUS_TEXT[l.status]}</span>
      </header>
      {l.kommentar && <p className="zelle-hinweis">Anmerkung: {l.kommentar}</p>}
      {l.entscheidungText && (
        <div className={l.status === 'nachforderung' ? 'hinweis-kasten' : 'zelle-hinweis'} role={l.status === 'nachforderung' ? 'note' : undefined}>
          <strong>{l.status === 'nachforderung' ? 'Bitte nachreichen: ' : 'Hinweis von GQL: '}</strong>
          {l.entscheidungText}
        </div>
      )}
      <ul className="dok-liste">
        {l.dokumente.map((d) => (
          <DokumentZeile key={d.id} d={d} />
        ))}
      </ul>
      {kinder}
    </article>
  );
}

/** Lädt die Lieferungen eines Standorts; fragt erneut ab, solange ausgewertet wird. */
export function useLieferungen(pfad: string, neuLaden: number, beiAbschluss?: () => void) {
  const [liste, setListe] = useState<DbPortalLieferungListe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const vorher = useRef(false);
  const runden = useRef(0);
  const laden = useCallback(async () => {
    try {
      const l = await api<DbPortalLieferungListe>(pfad);
      setListe(l);
      setFehler(null);
      if (vorher.current && !l.inArbeit) beiAbschluss?.();
      vorher.current = l.inArbeit;
      runden.current = l.inArbeit ? runden.current + 1 : 0;
    } catch (f) {
      setFehler((f as Error).message);
    }
  }, [pfad, beiAbschluss]);
  useEffect(() => {
    void laden();
  }, [laden, neuLaden]);
  useEffect(() => {
    if (!liste?.inArbeit) return;
    // Erst zügig, dann seltener nachfragen (lange OCR-Läufe sollen den Server nicht dauernd beschäftigen)
    const t = setTimeout(() => void laden(), Math.min(2000 + runden.current * 1000, 15_000));
    return () => clearTimeout(t);
  }, [liste, laden]);
  return { liste, fehler, laden };
}

export function LieferungenEinesStandorts({ standortId, neuLaden, beiAbschluss }: { standortId: string; neuLaden: number; beiAbschluss: () => void }) {
  const { liste, fehler } = useLieferungen(`/standorte/${standortId}/lieferungen`, neuLaden, beiAbschluss);
  if (fehler === 'Der Upload ist noch nicht eingerichtet.') return null;
  if (fehler) return <p className="fehlertext">{fehler}</p>;
  if (!liste) return null;
  if (!liste.lieferungen.length) return <p className="zelle-hinweis">Noch keine Unterlagen hochgeladen.</p>;
  return (
    <div className="lieferungen" aria-live="polite">
      {liste.lieferungen.map((l) => (
        <LieferungKarte key={l.id} l={l} />
      ))}
    </div>
  );
}

/** Entscheidung durch GQL mit Textbausteinen (Nachforderung/Ablehnung sind für den Nutzer sichtbar). */
export function Entscheidung({ l, nachher }: { l: DbPortalLieferung; nachher: () => void }) {
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const offen = l.status === 'offen' || l.status === 'nachforderung';
  const inArbeit = l.dokumente.some((d) => d.status === 'wartet' || d.status === 'in_arbeit');

  async function entscheiden(entscheidung: 'freigeben' | 'ablehnen' | 'nachfordern') {
    setLaeuft(true);
    setFehler(null);
    try {
      await api(`/admin/lieferungen/${l.id}/entscheidung`, { methode: 'POST', json: { entscheidung, ...(text.trim() ? { text: text.trim() } : {}) } });
      nachher();
    } catch (f) {
      setFehler((f as Error).message);
    } finally {
      setLaeuft(false);
    }
  }
  async function erneutUebertragen() {
    try {
      await api(`/admin/lieferungen/${l.id}/jurnodes`, { methode: 'POST' });
      nachher();
    } catch (f) {
      setFehler((f as Error).message);
    }
  }

  return (
    <div className="entscheidung">
      {!offen && (
        <p className="zelle-hinweis">
          {JN_TEXT[l.jnStatus]}
          {l.entschiedenAm && ` · entschieden ${zeitpunkt(l.entschiedenAm)}`}
          {l.status === 'freigegeben' && (l.jnStatus === 'fehler' || l.jnStatus === 'trocken') && (
            <>
              {' '}
              <button type="button" className="knopf-klein-text" onClick={() => void erneutUebertragen()}>
                Erneut übertragen
              </button>
            </>
          )}
        </p>
      )}
      {offen && (
        <>
          <label className="feld feld-breit">
            <span className="feld-label">Textbaustein</span>
            <select value="" onChange={(e) => e.target.value && setText(DB_PORTAL_TEXTBAUSTEINE[e.target.value] ?? '')}>
              <option value="">– auswählen –</option>
              <option value="unleserlich">Unleserlich</option>
              <option value="jahr_fehlt">Jahr fehlt</option>
              <option value="vertrag_fehlt">Vertrag fehlt</option>
              <option value="falscher_standort">Falscher Standort</option>
            </select>
          </label>
          <label className="feld feld-breit">
            <span className="feld-label">Text an den Nutzer (bei Nachforderung/Ablehnung Pflicht)</span>
            <textarea rows={3} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} />
          </label>
          <div className="knopfreihe">
            <button type="button" className="knopf" disabled={laeuft || inArbeit} onClick={() => void entscheiden('freigeben')}>
              Freigeben
            </button>
            <button type="button" className="knopf-sekundaer" disabled={laeuft || !text.trim()} onClick={() => void entscheiden('nachfordern')}>
              Nachfordern
            </button>
            <button type="button" className="knopf-sekundaer" disabled={laeuft || !text.trim()} onClick={() => void entscheiden('ablehnen')}>
              Ablehnen
            </button>
          </div>
          {inArbeit && <p className="zelle-hinweis">Freigabe möglich, sobald alle Dokumente ausgewertet sind.</p>}
        </>
      )}
      {fehler && (
        <p className="fehlertext" role="alert">
          {fehler}
        </p>
      )}
    </div>
  );
}
